import React, { useState, useEffect, useRef } from "react";
import Link from "@qlik/qlik/src/components/Link";
// eslint-disable-next-line import/no-extraneous-dependencies
import scrollTo from "gatsby-plugin-smoothscroll";
import { useAppContext } from "@qlik/qlik/src/context/AppContext";
import { debounce } from "@qlik/qlik/src/lib/utils";
import * as styles from "./styles.module.css";

/**
 * GlossaryList component
 * @param {Object} data - The data for the glossary list.
 */
const GlossaryList = ({ data }) => {
    // Define the filter for the glossary items
    const glossaryItemFilter = "ABCDEFGHIJKLMNOPQRSTUVWXYZ";
    const { cardCollections = [], customId } = data ?? {};
    // Map the data to get the glossary items
    const glossaryItems = cardCollections.map((item) => item.title);
    // State for the active letter
    const [activeFilterItem, setActiveFilterItem] = useState(null);
    const { notificationExists, headerHeight } = useAppContext();
    const [scrollFlag, setScrollFlag] = useState(false);
    const [cardRefs, setCardRefs] = useState([]);
    const glossaryFilter = useRef();
    const glossaryCards = useRef();
    const [topMargin, setTopMargin] = useState(0);
    const [bottomMargin, setBottomMargin] = useState(0);
    const DEBOUNCE_THRESHOLD = 500;
    const [isMobile, setIsMobile] = useState(false);

    useEffect(() => {
        const tMargin = headerHeight + glossaryFilter.current.clientHeight + 30;
        setTopMargin(tMargin);
        setBottomMargin(window.innerHeight - tMargin);
    }, [headerHeight]);

    /**
     * Handle the click event on a letter
     * @param {string} filterItem - The letter that was clicked.
     */
    const handleLetterClick = (filterItem) => {
        setScrollFlag(true);
        setTimeout(() => {
            setScrollFlag(false);
        }, 1000);

        if (filterItem !== activeFilterItem) {
            scrollTo(`#${filterItem}`);
            setActiveFilterItem(filterItem);
        }
    };

    const glossaryFilterClasses = notificationExists ? "xl:top-35" : "xl:top-21";

    useEffect(() => {
        setCardRefs((refs) =>
            Array(cardCollections.length)
                .fill()
                .map((_, i) => refs[i] || React.createRef())
        );
    }, [cardCollections.length]);

    useEffect(() => {
        const checkIsMobile = () => {
            if (isMobile !== window.innerWidth <= 1199) {
                setIsMobile(window.innerWidth <= 1199);
            }
        };

        checkIsMobile();
    }, []);

    const changeFilterActiveItem = (activeItem) => {
        setActiveFilterItem(activeItem);
        if (isMobile) {
            const activeElement = document.querySelector(`.glossaryListFilter [href="#${activeItem}"]`);
            activeElement.scrollIntoView({ inline: "center" });
        }
    };

    const getLinkDestination = (card) => {
        if (!card?.cta) return "#";
        const { internalLink, externalUrl } = card?.cta?.[0] || {};
        if (internalLink?.slug) {
            return internalLink.slug;
        } else if (externalUrl) {
            return externalUrl;
        }
        return "#";
    };

    useEffect(() => {
        const observer = new IntersectionObserver(
            (entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        if (!scrollFlag) {
                            debounce(changeFilterActiveItem(entry.target.id), DEBOUNCE_THRESHOLD);
                        }
                    }
                });
            },
            { threshold: 0, rootMargin: `-${topMargin}px 0px -${bottomMargin}px 0px` }
        );

        cardRefs?.forEach((ref) => {
            if (ref.current) {
                observer.observe(ref.current);
            }
        });

        return () => {
            if (cardRefs?.length > 0) {
                cardRefs?.forEach((ref) => {
                    if (ref.current) {
                        observer.unobserve(ref.current);
                    }
                });
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [cardRefs, scrollFlag]);

    return (
        <section className="mb-5" id={customId}>
            <div className="container">
                <div
                    ref={glossaryFilter}
                    className={`row sticky top-15 -mx-10 sm:mx-auto pl-6.5 sm:pl-0 xl:top-21 z-1 bg-white pb-5 pt-5 lg:pt-5.75 xl:pt-15 xl:transition-all xl:ease-in xl:duration-200 ${glossaryFilterClasses}`}
                >
                    <div className={`${styles.glossaryListFilter} glossaryListFilter scrollbar-hide px-3.75 xl:px-0`}>
                        <ul className="flex flex-row justify-start flex-nowrap scroll-smooth xl:flex-wrap gap-10 xl:gap-8 py-5 m-0 xl:justify-between">
                            {glossaryItemFilter.split("").map((filterItem, index) => {
                                // Check if the letter exists in the data
                                cardCollections?.includes(filterItem);
                                const glossaryItemExist = glossaryItems.includes(filterItem);
                                return (
                                    <li key={filterItem} className="p-0 after:hidden last:pr-5 sm:last:pr-0">
                                        {glossaryItemExist ? (
                                            <a
                                                className={`text-gray font-normal text-2xl leading-relaxed tracking-tight11 relative after:content-[''] after:bg-green after:absolute after:opacity-0 after:-top-2.25 after:-bottom-[5px] after:-left-[14px] after:-right-[14px] after:-z-1 hover:text-white hover:no-underline ${
                                                    activeFilterItem === filterItem ||
                                                    (index === 0 && activeFilterItem == null)
                                                        ? "active"
                                                        : ""
                                                }`}
                                                href={`#${filterItem}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    handleLetterClick(filterItem);
                                                }}
                                            >
                                                {filterItem}
                                            </a>
                                        ) : (
                                            <span className="text-stone font-normal text-2xl leading-relaxed tracking-tight11">
                                                {filterItem}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                </div>
                <div className="row">
                    <div className="mt-10" ref={glossaryCards}>
                        {cardCollections?.map((item, index) => (
                            <div
                                key={item.title}
                                id={item.title}
                                ref={cardRefs[index]}
                                className={`bg-stone-light rounded md:rounded-md p-7.5 mb-14.5 scroll-mt-[270px] `}
                            >
                                <div className="flex items-center mb-7.5 gap-7.5">
                                    {item.title && <h2>{item.title}</h2>}
                                    <div className="bg-stone w-full h-0.25" />
                                </div>
                                <div className="flex flex-wrap md:gap-x-30 xl:gap-x-20 gap-y-7 w-full">
                                    {item.cardItems?.map((card) => (
                                        <div className="col-1 md:col-3 min-w-full lg:min-w-[316px]" key={card?.id}>
                                            <Link
                                                className="text-xl grey-link-large mb-3.75 leading-tight cursor-pointer font-semibold"
                                                data-test={getLinkDestination(card)}
                                                to={getLinkDestination(card)}
                                            >
                                                <h3 className={styles.termHeading}>{card?.title}</h3>
                                            </Link>
                                            <p className="md:text-md leading-snug">{card?.description?.description}</p>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default GlossaryList;
